<template>
  <div class="vaimo-truncated-text" :class="{ animate: animatedText }">
    <template v-if="doubleText && renderedTextLong.length">
      <!-- eslint-disable vue/no-v-html -->
      <div
        ref="textElem"
        :class="textClasses"
        v-html="$dompurify(renderedText)"
      />
      <div v-if="!isTruncated" v-html="$dompurify(renderedTextLong)" />
      <button
        class="vaimo-truncated-text__readmore"
        @click.prevent="readMore()"
      >
        {{ buttonText }}
      </button>
    </template>

    <template v-else>
      <template v-if="truncate === 'by lines'">
        <div
          ref="textElem"
          :class="textClasses"
          v-html="$dompurify(renderedText)"
        />
        <button
          v-if="truncatedText && linesCount > lines"
          class="vaimo-truncated-text__readmore"
          @click.prevent="readMore()"
        >
          {{ buttonText }}
        </button>
      </template>

      <template v-else>
        <span
          class="vaimo-truncated-text__text"
          :class="textClasses"
          aria-live="polite"
          v-html="$dompurify(renderedText)"
        />
        <!--eslint-enable-->
        <template v-if="truncatedText">
          <VaimoButton
            v-if="alternativeButton"
            :class="alternativeButton.class || ''"
            :variant="alternativeButton.variant || ''"
            @click.prevent="readMore()"
          >
            {{ buttonText }}
          </VaimoButton>
          <button
            v-else
            class="vaimo-truncated-text__readmore"
            @click.prevent="readMore()"
          >
            {{ buttonText }}
          </button>
        </template>
      </template>
    </template>
  </div>
</template>

<script>
import {
  computed,
  defineComponent,
  ref,
  toRefs,
  useContext
} from '@nuxtjs/composition-api';

import { useLinesCounter } from '~/diptyqueTheme/composable';
import { Logger } from '~/helpers/logger';
export default defineComponent({
  name: 'VaimoText',
  components: {
    VaimoButton: () => import('atoms/VaimoButton.vue')
  },
  props: {
    text: {
      type: String,
      required: true
    },
    // eslint-disable-next-line vue/require-default-prop
    textLong: {
      type: String,
      required: false
    },
    truncate: {
      type: String,
      default: 'none',
      validator: (value) =>
        [
          'none',
          'by characters',
          'by lines',
          'by teaser',
          'by characters then lines'
        ].includes(value.toLowerCase())
    },
    characters: {
      type: Number,
      default: null
    },
    lines: {
      type: Number,
      default: null
    },
    teaser: {
      type: String,
      default: null
    },
    markdown: {
      type: Boolean,
      default: false
    },
    animatedText: {
      type: Boolean,
      default: false
    },
    doubleText: {
      type: Boolean,
      default: false
    },
    extraTextClasses: {
      type: String,
      default: ''
    },
    alternativeButton: {
      type: Object,
      default: null
    }
  },
  setup(props, { root }) {
    const { i18n } = useContext();
    const $markdown = root?.$markdown;
    const { text, textLong, truncate, characters, lines, teaser } =
      toRefs(props);
    const isTruncated = ref(truncate.value !== 'none');

    const truncatedText = computed(() => {
      if (
        truncate.value === 'by characters' ||
        truncate.value === 'by characters then lines'
      ) {
        if (text.value.length <= characters.value) {
          return null;
        }
        let trimmedString = text.value.substring(0, characters.value);
        trimmedString = trimmedString.substring(
          0,
          Math.min(trimmedString.length, trimmedString.lastIndexOf(' '))
        );
        return trimmedString + '...';
      }
      if (truncate.value === 'by lines') return text.value;
      if (truncate.value === 'by teaser') return teaser.value + '...';
      return null;
    });

    const processText = (
      text = '',
      isMarkdown = false,
      isAnimatedText = false
    ) => {
      let result = text;

      try {
        if (isMarkdown) {
          result = $markdown.render(result);
        }
        if (isAnimatedText) {
          result = result
            .split(/(<[^>]+>|\s+)/) // Split into HTML tags, spaces, or words
            .filter((part) => part.trim() !== '' || part === ' ') // Keep spaces intact
            .map((part) => {
              if (part.startsWith('<') && part.endsWith('>')) {
                return part; // HTML tags remain unchanged
              }
              if (part.trim() === '') {
                return part; // Spaces remain unchanged
              }
              return `<span class="animate-description">${part}</span>`;
            })
            .join('');
        }
      } catch (error) {
        Logger.error('VaimoTruncatedText: Error processing text', error);
      }

      return result;
    };

    const renderedText = computed(() => {
      const baseText = isTruncated.value
        ? truncatedText.value || text.value
        : text.value;

      return processText(baseText, props.markdown, props.animatedText);
    });

    const renderedTextLong = computed(() =>
      processText(textLong.value, props.markdown, props.animatedText)
    );

    const textClasses = computed(() => {
      const list = [
        `vaimo-truncated-text--truncated-${truncate.value.replaceAll(' ', '-')}`
      ];
      if (isTruncated.value) list.push('is-truncated');
      if (showLineClamp()) list.push(`line-truncate-${lines.value}`);
      if (props.extraTextClasses.length) list.push(props.extraTextClasses);

      return list;
    });

    const showLineClamp = () => {
      if (!lines.value) return false;
      const truncatedByLine = props.truncate === 'by lines';
      const truncatedByCharactersAndLines =
        props.truncate === 'by characters then lines';
      const hasTruncatedText = Boolean(truncatedText.value);
      return (
        truncatedByLine || (truncatedByCharactersAndLines && hasTruncatedText)
      );
    };

    const readMore = () => {
      isTruncated.value = !isTruncated.value;
    };

    const textElem = ref();
    const textElemScrollHeight = computed(
      // eslint-disable-next-line no-magic-numbers
      () => textElem.value?.scrollHeight - 10
    );
    const textElemClientHeight = computed(() => textElem.value?.clientHeight);
    const isReadMoreVisible = computed(() => {
      return textElemScrollHeight.value > textElemClientHeight.value;
    });
    const buttonText = computed(() =>
      isTruncated.value ? i18n.t('Read more') : i18n.t('Read less')
    );

    const { linesCount } = useLinesCounter(
      props.truncate === 'by lines' ? textElem : null
    );

    return {
      linesCount,
      isTruncated,
      renderedText,
      renderedTextLong,
      textClasses,
      readMore,
      textElem,
      isReadMoreVisible,
      textElemScrollHeight,
      textElemClientHeight,
      truncatedText,
      buttonText
    };
  }
});
</script>

<style lang="scss" scoped>
.vaimo-truncated-text {
  @apply relative;

  &__text {
    @apply whitespace-pre-wrap;
  }

  &__readmore {
    @apply body-l-light text-gris cursor-pointer whitespace-nowrap underline underline-offset-[2px];
  }

  &--truncated-by-lines,
  &--truncated-by-characters-then-lines {
    &.is-truncated {
      @apply overflow-hidden text-ellipsis;
      display: -webkit-box;
      -webkit-box-orient: vertical;

      &::v-deep p {
        @apply inline;
      }

      & + .vaimo-truncated-text__readmore {
        @apply absolute inline-block right-0 bottom-0 bg-blanc;
        padding-right: inherit;

        &:before {
          @apply inline-block absolute w-[15px] left-[-15px] bottom-[-1px] text-primary bg-blanc content-['...'];
        }
      }
    }
  }

  &--truncated-by-characters-then-lines {
    display: -webkit-box;
  }

  ::v-deep h6 {
    @apply paragraph-5;
  }
}
</style>

<style lang="scss">
.bg_gris-light .vaimo-truncated-text {
  &--truncated-by-lines,
  &--truncated-by-characters-then-lines {
    &.is-truncated {
      & + .vaimo-truncated-text__readmore {
        @apply bg-gris_light;

        &:before {
          @apply bg-gris_light;
        }
      }
    }
  }
}
</style>
